import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout, selectAuth } from "../features/auth/authSlice";
import { selectTheme, setTheme, THEMES } from "../features/ui/uiSlice";
import { updateMe } from "../features/auth/authSlice";
import { ICONS } from "../shared/icons";
import "./AppHeader.css";

/**
 * Верхняя полоса приложения (AUFB-67).
 *
 * Блок пользователя переехал сюда из подвала сайдбара: при 22 пунктах меню
 * подвал отнимал高оту у навигации и упирался в список. Наверху он к тому же
 * привычнее — там его ищут в большинстве CRM.
 *
 * Полоса намеренно узкая и без заголовка страницы: заголовок остаётся
 * внутри самой страницы, иначе он дублировался бы на каждом экране.
 */
export function AppHeader() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector(selectAuth);
  const theme = useSelector(selectTheme);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const displayName = useMemo(() => {
    const first = auth.user?.first_name?.trim() || "";
    const last = auth.user?.last_name?.trim() || "";
    const full = `${first} ${last}`.trim();
    return full || auth.user?.username || "User";
  }, [auth.user]);

  const initial = displayName.charAt(0).toUpperCase();
  const roleName = auth.user?.groups?.[0] || "staff";

  // Клик вне меню и Esc закрывают его.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onLogout = () => {
    dispatch(logout());
    navigate("/auth/login", { replace: true });
  };

  const onToggleTheme = () => {
    const next = theme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    // Оптимистично: тема должна переключаться мгновенно, не дожидаясь сети.
    dispatch(setTheme(next));
    dispatch(updateMe({ theme: next }));
  };

  return (
    <header className="app-header">
      <div className="app-header-spacer" />

      <button
        type="button"
        className="app-header-icon-btn"
        onClick={onToggleTheme}
        title={theme === THEMES.DARK ? "Светлая тема" : "Тёмная тема"}
        aria-label="Переключить тему"
      >
        {theme === THEMES.DARK ? ICONS.sun : ICONS.moon}
      </button>

      <div className="app-header-user" ref={wrapRef}>
        <button
          type="button"
          className="app-header-user-btn"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          <span className="app-header-avatar">{initial}</span>
          <span className="app-header-who">
            <span className="app-header-name">{displayName}</span>
            <span className="app-header-role">{roleName}</span>
          </span>
          <span className={`app-header-caret${open ? " is-open" : ""}`}>
            {ICONS.chevron}
          </span>
        </button>

        {open && (
          <div className="app-header-menu" role="menu">
            <button
              type="button"
              className="app-header-menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate("/settings");
              }}
            >
              <span className="app-header-menu-icon">{ICONS.settings}</span>
              Настройки
            </button>
            <div className="app-header-menu-sep" />
            <button
              type="button"
              className="app-header-menu-item is-danger"
              role="menuitem"
              onClick={onLogout}
            >
              <span className="app-header-menu-icon">{ICONS.logout}</span>
              Выйти
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export default AppHeader;
